import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Search, X, PlusCircle, Edit } from 'lucide-react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchShopLogins, createShopLogin, updateShopLogin } from '../services/shopLoginService';
import { fetchShops } from '../services/shopService';

const defaultForm = {
  shopId: '',
  username: '',
  email: '',
  phone: '',
  password: '',
  isActive: '1',
};

export default function ShopLogins() {
  const [logins, setLogins] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [shops, setShops] = useState([]);

  const loadLogins = async (params = {}) => {
    try {
      setLoading(true);
      const res = await fetchShopLogins(params);
      const records = res.data?.data?.records || res.data?.data || [];
      setLogins(records);
    } catch (error) {
      toast.error('Failed to load shop logins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogins();
  }, []);

  useEffect(() => {
    const loadShops = async () => {
      try {
        const res = await fetchShops({ limit: 200 });
        const records = res.data?.data?.records || res.data?.data || [];
        setShops(records);
      } catch (error) {
        toast.error('Failed to load shops');
      }
    };

    loadShops();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    const defaultShopId = shops.length ? String(shops[0].id) : '';
    setForm({ ...defaultForm, shopId: defaultShopId });
    setModalOpen(true);
  };

  const openEdit = (login) => {
    setEditingId(login.id);
    setForm({
      shopId: String(login.shopId || login.shop?.id || ''),
      username: login.username || '',
      email: login.email || '',
      phone: login.phone || '',
      password: '',
      isActive: login.isActive ? '1' : '0',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(defaultForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!form.shopId) {
        toast.error('Select a shop');
        return;
      }

      if (!editingId && !form.password) {
        toast.error('Password is required');
        return;
      }

      const payload = {
        shopId: Number(form.shopId),
        username: form.username || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        isActive: form.isActive === '1',
      };

      if (form.password) {
        payload.password = form.password;
      }

      if (editingId) {
        await updateShopLogin(editingId, payload);
        toast.success('Login updated');
      } else {
        await createShopLogin(payload);
        toast.success('Login created');
      }

      closeModal();
      loadLogins({ search });
    } catch (error) {
      toast.error('Failed to save login');
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Shop Logins</h1>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center space-x-1 px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500"
        >
          <PlusCircle size={18} />
          <span>Add Login</span>
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadLogins({ search });
          }}
          className="flex items-center relative"
        >
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by shop, email, or phone"
            className="border px-3 py-2 pl-8 pr-8 rounded w-full md:w-72 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                loadLogins();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <X size={16} />
            </button>
          )}
        </form>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th className="py-2 px-3">Shop</th>
              <th className="py-2 px-3">Username</th>
              <th className="py-2 px-3">Email</th>
              <th className="py-2 px-3">Phone</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-4 px-3 text-center text-sm text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : logins.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-4 px-3 text-center text-sm text-gray-500">
                  No records found
                </td>
              </tr>
            ) : (
              logins.map((login) => (
                <tr key={login.id} className="border-t dark:border-gray-700">
                  <td className="py-2 px-3">{login.shop?.name || ''}</td>
                  <td className="py-2 px-3">{login.username || '-'}</td>
                  <td className="py-2 px-3">{login.email || '-'}</td>
                  <td className="py-2 px-3">{login.phone || '-'}</td>
                  <td className="py-2 px-3">{login.isActive ? 'Active' : 'Inactive'}</td>
                  <td className="py-2 px-3">
                    <button
                      type="button"
                      className="text-indigo-600"
                      onClick={() => openEdit(login)}
                    >
                      <Edit size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 flex justify-end z-50">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full sm:w-96 h-full p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {editingId ? 'Edit Shop Login' : 'Add Shop Login'}
              </h2>
              <button type="button" onClick={closeModal} className="text-gray-500">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Shop</label>
                <select
                  value={form.shopId}
                  onChange={(e) => setForm({ ...form, shopId: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  disabled={!!editingId}
                >
                  <option value="">Select shop</option>
                  {shops.map((shop) => (
                    <option key={shop.id} value={shop.id}>
                      {shop.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Username</label>
                <input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {editingId ? 'New Password (optional)' : 'Password'}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500"
                >
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}
